import liveChatCRUDActions from '@mm/state/actions/LiveChatCRUDActions';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import {
  activeCompanyUsers,
  selectActiveUserId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useContextSelector } from 'use-context-selector';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useOneTimeConfirmationModal } from '../../../../../common/hooks/use-one-time-confirmation-modal';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import { ProfileImage } from '../ProfileImage/ProfileImage';
import './ConversationAssignmentDropdown.pcss';

export const useConversationAssignmentOptions = () => {
  const remoteFbPageRailsId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.id,
  );

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState] = useActor(lcService);

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedConversation = selectedContact?.conversation;

  const activeUserId = useSelector(selectActiveUserId);
  const companyUsers = useSelector(activeCompanyUsers);

  const usersWithAccess = (user: CompanyUsersModel) => {
    if (
      !['facebook_page_collaborator', 'live_chat_agent'].find(
        r => r === user.roleCode,
      )
    ) {
      return true;
    }
    if (
      selectedConversation?.assignedUserId?.toString() === user.id.toString()
    ) {
      return true;
    }
    if (!remoteFbPageRailsId) {
      return true;
    }
    return (user.allowedFacebookPageIds || []).includes(remoteFbPageRailsId);
  };

  const assignmentOptions: { id: string; name: string }[] = [
    { id: activeUserId.toString(), name: 'Assign to me' },
    ...companyUsers
      .filter(u => u.id !== activeUserId)
      .filter(usersWithAccess)
      .map(u => ({
        id: u.id.toString(),
        name: `${u.firstName} ${u.lastName}`,
      })),
    { id: '', name: 'Unassigned' },
  ];

  return assignmentOptions;
};

export const ConversationAssignmentDropdown = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId, remoteFbPageId = '' } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const selectedConversation = selectedContact?.conversation;

  const companyUsers = useSelector(activeCompanyUsers);

  const assignmentOptions = useConversationAssignmentOptions();

  const confirmationModal = useOneTimeConfirmationModal(
    'inbox_linked_convo_assignment',
    {
      messageText: 'All linked contacts will be assigned to this agent',
      headerText: 'Assigning Linked Contact',
      okButtonText: 'Assign Conversations',
      cancelButtonFirst: true,
    },
  );

  const customOptionRenderer = (
    option: DropdownOption,
    activeItem?: boolean,
  ) => {
    const user = companyUsers.find(u => u.id.toString() === option.id);
    const displayText =
      activeItem && user ? `${user.firstName} ${user.lastName}` : option.name;
    return (
      <div className="conversation-assignment-dropdown__option">
        <ProfileImage
          className="conversation-assignment-dropdown__option-profile-image"
          altText=""
          profilePictureUrl={user?.image}
        />
        {displayText}
      </div>
    );
  };

  if (!remoteUserId) {
    if (selectedContact.loading) {
      return (
        <PrimarySelect
          className="conversation-assignment-dropdown conversation-assignment-dropdown--loading"
          activeItemClassName="conversation-assignment-dropdown__active-item"
          options={[]}
          value={{ id: 0, name: 'Loading...' }}
          onChange={() => undefined}
          dropdownWidth="auto"
          disabled={true}
          customOptionRenderer={customOptionRenderer}
        />
      );
    }

    return null;
  }

  const getSelectedOption = () => {
    if (selectedConversation?.assignedUserId) {
      return assignmentOptions.find(
        o => o.id === selectedConversation.assignedUserId?.toString(),
      );
    }
    return assignmentOptions[assignmentOptions.length - 1];
  };

  const onChange = (option: DropdownOption) => {
    const assignConvo = async () => {
      await dispatch(
        liveChatCRUDActions.updateSingleConversationStatus({
          remotePageId: remoteFbPageId,
          remoteUserIds: [remoteUserId],
          assignedUserId: option.id.toString(),
        }),
      );

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFRESH',
        payload: [remoteUserId],
      });
      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFILTER',
        payload: [remoteUserId],
      });
      refreshSelectedContact();
    };

    if (selectedContact.linkedContacts?.length) {
      confirmationModal({
        onConfirm: assignConvo,
      });
    } else {
      assignConvo();
    }
  };

  return (
    <PrimarySelect
      className="conversation-assignment-dropdown"
      options={assignmentOptions}
      value={getSelectedOption()}
      onChange={onChange}
      customOptionRenderer={customOptionRenderer}
      dropdownWidth="auto"
      disabled={selectedContact.loading}
    />
  );
};
